import type { RiskTypeConfig, TreesAggregateData } from './types';
import { LayerType } from './types';
import { IconLayer, GeoJsonLayer } from '@deck.gl/layers';
import type { IconLayerProps } from '@deck.gl/layers';
import { CompositeLayer, LinearInterpolator } from '@deck.gl/core';
import type {
  Position,
  Color,
  PickingInfo,
  Layer,
  Viewport,
} from '@deck.gl/core';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { isiOS } from '@/views/map/utils';
import { hexToRgba } from '@/helpers/colors';
import type { Feature } from 'geojson';

import colorbrewerDefault from 'colorbrewer/index.es.js';
import type colorbrewerType from 'colorbrewer';

// Cast the default export to the proper type for better TypeScript support
// This is all necessary because of https://github.com/saikocat/colorbrewer/issues/14#issuecomment-3032528042
const colorbrewer = colorbrewerDefault as typeof colorbrewerType;

export const TREE_POINTS_ZOOM = 13;
export const TREE_TYPES = ['strike', 'declining', 'hazard'];

const HEATMAP_ZOOM = 13;
const ICON_MAPPING_TREES = {
  strike: { x: 0, y: 0, width: 128, height: 128, mask: false },
  declining: { x: 128, y: 0, width: 128, height: 128, mask: false },
  hazard: { x: 256, y: 0, width: 128, height: 128, mask: false },
};

const getTreeAggregateLayer = (
  id: string,
  data: TreesAggregateData[],
  showHazardTrees: boolean,
  showDecliningTrees: boolean,
  showStrikeTrees: boolean,
  isSmallScreen?: boolean,
  minZoom = 5,
  maxZoom = 13
) => {
  const colorRangeiOS: Color[] = [
    [245, 255, 178],
    [250, 230, 178],
    [240, 200, 118],
    [250, 217, 118],
    [230, 178, 76],
    [240, 141, 60],
    [210, 59, 32],
    [215, 59, 32],
    [159, 0, 38],
  ];

  const isiOSMobile = isSmallScreen && isiOS;
  const getColors: Color[] = colorbrewer.YlOrRd[6].map((d) => {
    const color = hexToRgba(d);
    return [color.r, color.g, color.b];
  });

  const props = {
    data,
    id,
    visible: showHazardTrees || showDecliningTrees || showStrikeTrees,
    minZoom,
    maxZoom,
    getWeight: (d: TreesAggregateData) => {
      let count = 0;
      if (showDecliningTrees) {
        count += d.counts.decliningCount;
      }
      if (showHazardTrees) {
        count += 2 * d.counts.hazardCount;
      }
      if (showStrikeTrees) {
        count += d.counts.strikeCount;
      }
      return count;
    },
    getPosition: (d: TreesAggregateData): Position => {
      const bounds = d.bounds;
      const mins = bounds[0];
      const maxs = bounds[1];
      const long = (maxs[0] + mins[0]) / 2;
      const lat = (maxs[1] + mins[1]) / 2;
      return [long, lat];
    },
    weightsTextureSize: 512, //Impacts the rendering performance.
    colorRange: isiOSMobile ? colorRangeiOS : getColors,
    intensity: isiOSMobile ? 0.7 : 1, //A lower number than 1 tends to choose the colorRange more towards the lower end of the spectrum
    debounceTimeout: 1000,
    radiusPixels: isiOSMobile ? 10 : 30, //Setting this to a lower value will reduce the patchiness of colors in mobile
    threshold: 0.2,
    aggregation: 'SUM' as 'SUM' | 'MEAN',
    refinementStrategy: 'no-overlap',
    pickable: false,
    updateTriggers: {
      getWeight: [showHazardTrees, showDecliningTrees, showStrikeTrees],
    },
  };

  return new HeatmapLayer(props);
};

type FallInTreesLayerProps = {
  id: string;
  visible: boolean;
  pickable: boolean;
  data: Feature[];
  aggregateData: TreesAggregateData[];
  isHazardTreesOnly: boolean;
  showHazardTrees: boolean;
  showDecliningTrees: boolean;
  showStrikeTrees: boolean;
  showHeatmap: boolean;
  isSmallScreen: boolean;
  minZoom: number;
  maxZoom: number;
  autoHighlight: boolean;
};

export const getTreesInDeclineLayer = (
  treesInDeclineConfig: RiskTypeConfig,
  data: Feature[],
  aggregateData: TreesAggregateData[],
  isSmallScreen = false,
  minZoom = 5,
  maxZoom = 24
) => {
  const {
    visible,
    isHazardTreesOnly,
    showHazardTrees,
    showDecliningTrees,
    showStrikeTrees,
    showHeatmap,
  } = treesInDeclineConfig;
  const id = `${LayerType[LayerType.trees]}_points`;
  return new FallInTreesLayer({
    id,
    pickable: true,
    minZoom,
    maxZoom,
    data,
    aggregateData,
    visible,
    isHazardTreesOnly,
    showHazardTrees,
    showDecliningTrees,
    showStrikeTrees,
    showHeatmap,
    isSmallScreen,
    autoHighlight: true,
  });
};

type TreeTypeConfig = {
  visible: boolean;
  getSize: number;
  data: Feature[];
  autoHighlight: boolean;
};

class FallInTreesLayer extends CompositeLayer<FallInTreesLayerProps> {
  // Since we don't have real types declared we need to override here
  isHazardTreesOnly: boolean;
  showHeatmap: boolean;

  constructor(props: FallInTreesLayerProps) {
    super(props);
    this.isHazardTreesOnly = props.isHazardTreesOnly;
    this.showHeatmap = props.showHeatmap;
  }
  static layerName = 'fallInHealthTrees';

  filterSubLayer({ layer, viewport }: { layer: Layer; viewport: Viewport }) {
    if (this.isHazardTreesOnly) {
      return layer.id === 'hazard';
    }
    const visibleLayers: string[] = [];
    if (viewport.zoom > TREE_POINTS_ZOOM) {
      visibleLayers.push(...TREE_TYPES);
    }
    if (viewport.zoom < HEATMAP_ZOOM && this.showHeatmap) {
      visibleLayers.push('tree-aggregate-layer');
    }
    return visibleLayers.includes(layer.id);
  }

  _updateAutoHighlight(info: PickingInfo): void {
    const isStrikeTree =
      info.object?.properties?.type === 'strike' &&
      info.sourceLayer?.id === 'strike';
    for (const layer of this.getSubLayers()) {
      if (layer.id === 'strike') {
        // Only update auto highlight info for strike trees. Other layers don't have autoHighlight and indexing is per tree type.
        if (isStrikeTree) {
          layer.updateAutoHighlight(info);
        } else {
          layer.updateAutoHighlight({
            ...info,
            index: -1,
            picked: false,
            object: undefined,
            color: null,
          });
        }
      }
    }
  }

  renderLayers() {
    const {
      data,
      aggregateData,
      minZoom,
      maxZoom,
      showHazardTrees,
      showDecliningTrees,
      showStrikeTrees,
      isSmallScreen,
    } = this.props;

    const commonConfig = {
      minZoom,
      maxZoom,
      pickable: true,
      iconAtlas: '/icons/trees.png',
      iconMapping: ICON_MAPPING_TREES,
      highlightColor: [49, 226, 188, 89],
      transitionDuration: 8000,
      transitionInterpolator: new LinearInterpolator(),
      sizeScale: 6,
      sizeMinPixels: 8,
      sizeMaxPixels: 12,
      alphaCutoff: 0.05,
      getColor: () => [220, 27, 26] as Color,
    };

    const treeTypeConfig = new Map<string, TreeTypeConfig>([
      [
        'strike',
        { getSize: 1, visible: showStrikeTrees, data: [], autoHighlight: true },
      ],
      [
        'declining',
        {
          getSize: 2,
          visible: showDecliningTrees,
          data: [],
          autoHighlight: false,
        },
      ],
      [
        'hazard',
        {
          getSize: 5,
          visible: showHazardTrees,
          data: [],
          autoHighlight: false,
        },
      ],
    ]);
    data.forEach((d) => {
      const transformedRecord = {
        ...d,
        position:
          d.properties?.centroid !== undefined
            ? getCentroidCoords(d.properties.centroid)
            : (d.geometry as GeoJSON.Point | GeoJSON.MultiPoint).coordinates,
      };
      treeTypeConfig.get(d.properties?.type)?.data.push(transformedRecord);
      return transformedRecord;
    });

    return [
      getTreeAggregateLayer(
        'tree-aggregate-layer',
        aggregateData,
        showHazardTrees,
        showDecliningTrees,
        showStrikeTrees,
        isSmallScreen
      ),
      ...Array.from(treeTypeConfig).map(([treeType, config]) => {
        const classType =
          treeType === 'strike' ? TreeCrownLayer : IconLayer<IconProps>;
        return new classType({
          id: treeType,
          ...commonConfig,
          ...config,
          getIcon: () => treeType,
        });
      }),
    ];
  }
}

const getCentroidCoords = (wkt: string) => {
  return wkt.match(/[-.0-9]+/g)?.map(parseFloat);
};

type TreeCrownProps = {
  id: string;
  data: Feature[];
  getSize: number;
  getIcon: () => string;
  minZoom: number;
  maxZoom: number;
  position?: number[];
};

type IconProps = {
  data: Feature;
} & IconLayerProps<Feature>;

class TreeCrownLayer extends CompositeLayer<TreeCrownProps> {
  constructor(props: TreeCrownProps) {
    super(props);
  }
  static layerName = 'treeCrowns';

  renderLayers() {
    const { id, data, minZoom, maxZoom, position, getSize, visible, getIcon } =
      this.props;

    const iconLayerProps = {
      data,
      minZoom,
      maxZoom,
      position,
      getSize,
      visible,
      getIcon,
      pickable: true,
      iconAtlas: '/icons/trees.png',
      autoHighlight: false,
      iconMapping: ICON_MAPPING_TREES,
      highlightColor: [49, 226, 188, 89],
      transitionDuration: 8000,
      transitionInterpolator: new LinearInterpolator(),
      sizeScale: 6,
      sizeMinPixels: 8,
      sizeMaxPixels: 12,
      alphaCutoff: 0.05,
      getColor: () => [220, 27, 26] as Color,
    };

    return [
      new GeoJsonLayer({
        data,
        id: `${id}_treeCrownOutline`,
        pickable: true,
        autoHighlight: true,
        getLineWidth: 0.2,
        highlightColor: [175, 236, 223, 230],
        getLineColor: [175, 236, 223, 0],
        stroked: true,
        filled: false,
      }),
      new GeoJsonLayer({
        data,
        id: `${id}_treeCrownOutlinePicking`,
        pickable: true,
        autoHighlight: false,
        getFillColor: [255, 255, 255, 0],
        stroked: false,
      }),
      new IconLayer<IconProps>({
        id: `${id}_treeCrownIcon`,
        ...iconLayerProps,
      }),
    ];
  }
}
